import { useEffect, useState } from 'react';

import { detectPlatform } from './detectPlatform';
import { selectByPlatform } from './select';
import type { PlatformInfo, PlatformSelectSpec } from './types';

/**
 * Reactive Platform.select on web — re-evaluates when the viewport crosses
 * breakpoint thresholds (phone ↔ tablet ↔ desktop). Pointer type changes
 * (`coarse` ↔ `fine`) also trigger updates. Per-pixel resizes are deduped.
 */
function breakpointKey(info: PlatformInfo): string {
  return `${info.isTablet}|${info.isPhone}|${info.isDesktop}|${info.isTouch}`;
}

export function usePlatformValue<T>(spec: PlatformSelectSpec<T>): T | undefined {
  const [info, setInfo] = useState<PlatformInfo>(() => detectPlatform());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => {
      const next = detectPlatform();
      setInfo((prev) => (breakpointKey(prev) === breakpointKey(next) ? prev : next));
    };

    window.addEventListener('resize', update);
    const mq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: coarse)')
        : null;
    mq?.addEventListener?.('change', update);

    return () => {
      window.removeEventListener('resize', update);
      mq?.removeEventListener?.('change', update);
    };
  }, []);

  return selectByPlatform(spec, info);
}
